import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useCart } from "../../contexts/CartContext";
import CustomerHeader from "../../components/CustomerHeader";
import CartDrawer from "../../components/CartDrawer";
import ProductCard from "../../components/ProductCard";
import { Timer, Minus, Plus, ArrowLeft, Shield, Truck } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [shop, setShop] = useState(null);
  const [related, setRelated] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const cart = useCart();

  useEffect(() => {
    (async () => {
      try {
        const prods = await api.get("/products");
        const p = prods.data.find((x) => x.product_id === id);
        if (!p) return navigate("/app");
        setProduct(p);
        const shops = await api.get("/shops");
        setShop(shops.data.find((s) => s.shop_id === p.shop_id));
        setRelated(prods.data.filter((x) => x.category === p.category && x.product_id !== p.product_id).slice(0, 10));
      } catch { navigate("/app"); }
    })();
  }, [id, navigate]);

  const inCart = product && cart.cart.items[product.product_id];

  if (!product) return <div className="light-scope min-h-screen bg-white"><CustomerHeader onCartClick={() => setShowCart(true)} /><div className="p-10 text-center text-slate-500">Loading…</div></div>;

  return (
    <div className="light-scope min-h-screen bg-white" data-testid="product-detail-container">
      <CustomerHeader onCartClick={() => setShowCart(true)} />
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="back-btn">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="rounded-2xl overflow-hidden bg-slate-50 border border-slate-200 aspect-square">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="inline-flex items-center gap-1 ls-pill-10min text-[11px] font-bold rounded-full px-2 py-0.5">
              <Timer size={11} /> Delivers in 10 mins · {shop?.name}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3 leading-tight tracking-tight">{product.name}</h1>
            <div className="text-slate-500 mt-1">{product.unit}</div>

            <div className="mt-5 flex items-end gap-3">
              <div className="text-3xl font-extrabold text-slate-900">₹{product.price}</div>
              {product.mrp && product.mrp > product.price && (
                <>
                  <div className="text-base text-slate-400 line-through mb-0.5">₹{product.mrp}</div>
                  <div className="text-sm font-bold text-emerald-600 mb-0.5">{Math.round(((product.mrp - product.price) / product.mrp) * 100)}% OFF</div>
                </>
              )}
            </div>
            <div className="text-xs text-slate-500">(inclusive of all taxes)</div>

            <div className="mt-6 flex items-center gap-3">
              {!inCart ? (
                <button
                  onClick={() => cart.addItem(product, shop)}
                  className="ls-add-btn rounded-xl px-6 py-3 text-base"
                  data-testid="detail-add-btn"
                >
                  ADD TO CART
                </button>
              ) : (
                <div className="inline-flex items-center rounded-xl overflow-hidden border border-emerald-500">
                  <button className="w-10 h-10 bg-emerald-600 text-white font-bold inline-flex items-center justify-center" onClick={() => cart.decrement(product.product_id)}><Minus size={16} /></button>
                  <div className="w-12 h-10 inline-flex items-center justify-center bg-emerald-600 text-white font-bold">{inCart.qty}</div>
                  <button className="w-10 h-10 bg-emerald-600 text-white font-bold inline-flex items-center justify-center" onClick={() => cart.addItem(product, shop)}><Plus size={16} /></button>
                </div>
              )}
              <button
                onClick={() => setShowCart(true)}
                className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white px-5 h-11 font-semibold"
                data-testid="detail-view-cart-btn"
              >
                View cart
              </button>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Perk icon={Truck} title="Free delivery" subtitle="On orders above ₹199" />
              <Perk icon={Shield} title="100% safe" subtitle="Sealed, hygienically packed" />
            </div>

            <div className="mt-8">
              <div className="font-bold text-slate-900">About this product</div>
              <p className="text-slate-600 text-sm leading-relaxed mt-2">
                {product.description || `${product.name} delivered fresh from a verified ${product.category.toLowerCase()} merchant near you. Packed and dispatched in minutes.`}
              </p>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="ls-section-h text-xl sm:text-2xl mb-3">You may also like</h2>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
              {related.map((p) => (
                <div key={p.product_id} className="w-44 sm:w-48 shrink-0">
                  <ProductCard product={p} shop={shop} variant="scroll" />
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
      <CartDrawer open={showCart} onClose={() => setShowCart(false)} />
    </div>
  );
}

function Perk({ icon: Icon, title, subtitle }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-emerald-600" />
        <div className="font-bold text-sm text-slate-900">{title}</div>
      </div>
      <div className="text-xs text-slate-600 mt-0.5">{subtitle}</div>
    </div>
  );
}
